import {
  Clipboard,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  XCircle,
} from "lucide-react";
import { useMemo } from "react";

import { useAppSelector } from "@/store/hooks";

interface Props {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: string;
  networkName?: string;
  loading?: boolean;
  valid?: boolean | null;
}

const ExternalAddressInput = ({
  value,
  onChange,
  disabled = false,
  error,
  networkName,
  loading = false,
  valid = null,
}: Props) => {
  const { theme } = useAppSelector((state) => state.ui);

  const detectedNetwork = useMemo(() => {
    const address = value.trim();

    if (!address) return null;

    // Ethereum / Polygon / BSC
    if (/^0x[a-fA-F0-9]{40}$/.test(address)) {
      return "EVM";
    }

    // Tron
    if (/^T[a-zA-Z0-9]{33}$/.test(address)) {
      return "TRON";
    }

    // Bitcoin Legacy
    if (/^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(address)) {
      return "BITCOIN";
    }

    // Bitcoin Segwit
    if (/^bc1[a-zA-HJ-NP-Z0-9]{25,87}$/.test(address)) {
      return "BITCOIN";
    }

    // Solana
    if (/^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(address)) {
      return "SOLANA";
    }

    return "UNKNOWN";
  }, [value]);

  const pasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();

      onChange(text.trim());
    } catch {
      // ignore
    }
  };

  return (
    <div className="space-y-3">
      <label className="text-sm text-muted-foreground">
        Destination Address
      </label>

      <div className="relative">
        <input
          type="text"
          disabled={disabled}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Paste destination wallet address"
          className={`w-full rounded-lg border px-4 py-3 pr-14 transition-colors ${
            theme === "dark"
              ? "bg-muted border-border text-foreground placeholder:text-muted-foreground"
              : "bg-white border-gray-200 text-gray-900 placeholder:text-gray-400"
          } ${
            disabled
              ? "opacity-50 cursor-not-allowed"
              : "focus:outline-none focus:border-primary"
          }`}
        />

        <button
          type="button"
          disabled={disabled}
          onClick={pasteFromClipboard}
          className="absolute right-3 top-1/2 -translate-y-1/2"
        >
          <Clipboard className="w-5 h-5 text-primary" />
        </button>
      </div>

      {value && (
        <div className="rounded-lg border p-3 space-y-3">
          <div className="flex items-center gap-2">
            {detectedNetwork === "UNKNOWN" ? (
              <>
                <AlertTriangle className="w-4 h-4 text-red-500" />

                <span className="text-sm text-red-500">
                  Unable to recognize this wallet address.
                </span>
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />

                <span className="text-sm">
                  Detected Address Type:
                  <strong className="ml-1">{detectedNetwork}</strong>
                </span>
              </>
            )}
          </div>

          {networkName && (
            <p className="text-xs text-muted-foreground">
              Selected Network:
              <span className="ml-1 font-semibold">{networkName}</span>
            </p>
          )}

          <div className="border-t pt-3">
            {loading && (
              <div className="flex items-center gap-2 text-sm text-yellow-500">
                <Loader2 className="h-4 w-4 animate-spin" />

                <span>Checking address...</span>
              </div>
            )}

            {!loading && valid === true && (
              <div className="flex items-center gap-2 text-sm text-emerald-500">
                <CheckCircle2 className="h-4 w-4" />

                <span>✓ Valid address</span>
              </div>
            )}

            {!loading && valid === false && (
              <div className="flex items-center gap-2 text-sm text-red-500">
                <XCircle className="h-4 w-4" />

                <span>✗ Invalid address</span>
              </div>
            )}
          </div>
        </div>
      )}

      {error && <p className="text-sm text-red-500">{error}</p>}
    </div>
  );
};

export default ExternalAddressInput;
